import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api'
import axios from 'axios'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { FilterMatchMode } from 'primereact/api'
import { Toast } from 'primereact/toast'
import { useAuth } from '../contexts/AuthContext'
import { Member } from 'shared'
import AddMemberModal from './AddMemberModal'
import EditMemberModal from './EditMemberModal'
import PendingMembersWorkflow from './PendingMembersWorkflow'
import PaymentHistoryModal from './PaymentHistoryModal'

const MemberViewer: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showPaymentHistory, setShowPaymentHistory] = useState(false)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const [paymentHistoryMember, setPaymentHistoryMember] = useState<Member | null>(null)
  const [globalFilterValue, setGlobalFilterValue] = useState('')
  const [filters, setFilters] = useState<any>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    memberNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    firstName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    lastName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    membershipStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
    paymentStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
    earnsIncome: { value: null, matchMode: FilterMatchMode.EQUALS }
  })

  const toast = React.useRef<Toast>(null)
  const { logout } = useAuth()


  useEffect(() => {
    fetchMembers()
  }, [])

  const fetchMembers = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get(`${API_BASE_URL}/api/members`)
      setMembers(response.data.data?.members || [])
    } catch (err: any) {
      console.error('Failed to fetch members:', err)
      if (err.response?.status === 401) {
        logout()
      } else {
        setError(err.response?.data?.message || 'Failed to load members')
      }
    } finally {
      setLoading(false)
    }
  }

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const _filters = { ...filters }
    _filters['global'].value = value

    setFilters(_filters)
    setGlobalFilterValue(value)
  }

  const clearFilter = () => {
    initFilters()
  }

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      memberNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
      firstName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      lastName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      email: { value: null, matchMode: FilterMatchMode.CONTAINS },
      membershipStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
      paymentStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
      earnsIncome: { value: null, matchMode: FilterMatchMode.EQUALS }
    })
    setGlobalFilterValue('')
  }

  const exportToCSV = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/members/export/csv`, {
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      link.download = `members_export_${timestamp}.csv`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Failed to export CSV:', err)
      if (err.response?.status === 401) {
        logout()
      } else {
        setError('Failed to export CSV file')
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE')
  }

  const statusBodyTemplate = (member: Member) => {
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold
        ${member.membershipStatus === 'ACTIVE' ? 'bg-green-100 text-green-800' :
          member.membershipStatus === 'INACTIVE' ? 'bg-red-100 text-red-800' :
            member.membershipStatus === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
              member.membershipStatus === 'TERMINATED' ? 'bg-gray-100 text-gray-800' :
                'bg-yellow-100 text-yellow-800'}`}>
        {member.membershipStatus}
      </span>
    )
  }

  const earningBodyTemplate = (member: Member) => {
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold
        ${member.earnsIncome ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
        {member.earnsIncome ? 'Yes' : 'No'}
      </span>
    )
  }

  const memberNumberBodyTemplate = (member: Member) => {
    return member.memberNumber?.toString().padStart(3, '0') || '000'
  }

  const dateBodyTemplate = (field: keyof Member) => (member: Member) => {
    const value = member[field]
    return value ? formatDate(value as string) : '-'
  }

  const actionsBodyTemplate = (member: Member) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-money-bill"
          className="p-button-rounded p-button-text p-button-info"
          tooltip="Payment History"
          tooltipOptions={{ position: 'left' }}
          onClick={(e) => {
            e.stopPropagation()
            setPaymentHistoryMember(member)
            setShowPaymentHistory(true)
          }}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-warning"
          tooltip="Edit Member"
          tooltipOptions={{ position: 'left' }}
          onClick={(e) => {
            e.stopPropagation()
            setSelectedMember(member)
            setShowEditModal(true)
          }}
        />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
          <div className="text-center p-4">
            <i className="pi pi-exclamation-triangle text-6xl text-red-500 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Members</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button
              label="Try Again"
              icon="pi pi-refresh"
              onClick={fetchMembers}
              className="p-button-outlined"
            />
          </div>
      </Card>
    )
  }

  const renderHeader = () => {
    return (
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <span className="text-sm text-gray-600">{members.length} members</span>
        <div className="flex gap-1 items-center">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Search..."
              className="p-inputtext-sm"
            />
          </span>
          <Button
            type="button"
            icon="pi pi-filter-slash"
            outlined
            onClick={clearFilter}
            className="p-button-sm"
            tooltip="Clear filters"
            tooltipOptions={{ position: 'bottom' }}
          />
        </div>
      </div>
    )
  }

  const header = renderHeader()

  return (
    <div className="space-y-4">
      <Toast ref={toast} />
      {/* Members Database Section */}
      <Card>
        <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Members Management</h2>
          <div className="flex flex-wrap gap-1">
            {selectedMember && (
              <Button
                icon="pi pi-pencil"
                onClick={() => setShowEditModal(true)}
                className="p-button-warning p-button-sm"
                tooltip="Edit Member"
                tooltipOptions={{ position: 'bottom' }}
              />
            )}
            <Button
              icon="pi pi-download"
              onClick={exportToCSV}
              className="p-button-info p-button-sm"
              disabled={members.length === 0}
              tooltip="Export CSV"
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-plus"
              onClick={() => setShowAddModal(true)}
              className="p-button-success p-button-sm"
              tooltip="Add Member"
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-refresh"
              onClick={fetchMembers}
              loading={loading}
              className="p-button-outlined p-button-sm"
              tooltip="Refresh"
              tooltipOptions={{ position: 'bottom' }}
            />
          </div>
        </div>

        <DataTable
          value={members}
          loading={loading}
          paginator
          rows={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          className="p-datatable-sm"
          header={header}
          filters={filters}
          onFilter={(e) => setFilters(e.filters as any)}
          globalFilterFields={['memberNumber', 'firstName', 'lastName', 'email', 'membershipStatus', 'paymentStatus']}
          emptyMessage="No members found"
          showGridlines
          stripedRows
          scrollable
          scrollHeight="600px"
          sortField="memberNumber"
          sortOrder={1}
          selectionMode="single"
          selection={selectedMember}
          onSelectionChange={(e) => setSelectedMember(e.value as Member | null)}
          dataKey="id"
          filterDisplay="row"
        >
          <Column
            header="Actions"
            body={actionsBodyTemplate}
            exportable={false}
            style={{ minWidth: '80px', width: 'auto' }}
          />
          <Column
            field="firstName"
            header="First Name"
            sortable
            filter
            filterPlaceholder="Search"
            style={{ minWidth: '100px' }}
          />
          <Column
            field="lastName"
            header="Last Name"
            sortable
            filter
            filterPlaceholder="Search"
            style={{ minWidth: '100px' }}
          />
          <Column
            field="email"
            header="Email"
            sortable
            filter
            filterPlaceholder="Search"
            style={{ minWidth: '150px' }}
          />
          <Column
            field="earnsIncome"
            header="Income"
            body={earningBodyTemplate}
            sortable
            filter
            filterElement={(options) => (
              <select
                value={options.value === null ? '' : options.value.toString()}
                onChange={(e) => options.filterCallback(e.target.value === '' ? null : e.target.value === 'true')}
                className="p-dropdown p-component"
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            )}
            style={{ minWidth: '80px' }}
          />
          <Column
            field="membershipStatus"
            header="Status"
            body={statusBodyTemplate}
            sortable
            filter
            filterElement={(options) => (
              <select
                value={options.value || ''}
                onChange={(e) => options.filterCallback(e.target.value || null)}
                className="p-dropdown p-component"
              >
                <option value="">All</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="TERMINATED">Terminated</option>
              </select>
            )}
            style={{ minWidth: '90px' }}
          />
          <Column
            field="joinDate"
            header="Joined"
            body={dateBodyTemplate('joinDate')}
            sortable
            style={{ minWidth: '90px' }}
          />
          <Column
            field="memberNumber"
            header="#"
            body={memberNumberBodyTemplate}
            sortable
            filter
            filterPlaceholder="Search"
            style={{ minWidth: '70px' }}
          />
        </DataTable>
      </Card>

      {/* Pending Members Workflow Section */}
      <PendingMembersWorkflow />

      <AddMemberModal
        visible={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchMembers()
          setShowAddModal(false)
        }}
      />

      <EditMemberModal
        visible={showEditModal}
        member={selectedMember}
        onHide={() => setShowEditModal(false)}
        onSuccess={() => {
          fetchMembers()
          setShowEditModal(false)
          setSelectedMember(null)
        }}
        onDelete={() => {
          fetchMembers()
          setShowEditModal(false)
          setSelectedMember(null)
        }}
      />

      <PaymentHistoryModal
        visible={showPaymentHistory}
        onHide={() => {
          setShowPaymentHistory(false)
          setPaymentHistoryMember(null)
        }}
        member={paymentHistoryMember}
      />
    </div>
  )
}

export default MemberViewer
